import { useState, useEffect } from 'react';
import AdminLayout from './AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { admin } from '../../utils/api';
import { toast } from 'sonner';
import { 
  DollarSign, CreditCard, Building2, Clock, CheckCircle, 
  AlertCircle, Send, RefreshCw, Search, TrendingUp, Wallet,
  Store, ArrowRight
} from 'lucide-react';

const PAYMENT_STATUS = {
  ready: { label: 'Ready for Payout', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  pending_setup: { label: 'Pending Setup', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  processing: { label: 'Processing', color: 'bg-blue-100 text-blue-800', icon: RefreshCw },
};

export default function AdminRetailerPayouts({ user, onLogout }) {
  const [retailers, setRetailers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRetailer, setSelectedRetailer] = useState(null);
  const [filter, setFilter] = useState('');
  const [payoutAmount, setPayoutAmount] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [totals, setTotals] = useState({ pending: 0, paid: 0, total: 0 });

  useEffect(() => {
    loadRetailers();
  }, []);

  const loadRetailers = async () => {
    try {
      setLoading(true);
      const response = await admin.retailerPayouts();
      const data = response.data;
      setRetailers(data.retailers || []);
      setTotals(data.totals || { pending: 0, paid: 0, total: 0 });
    } catch (error) {
      toast.error('Failed to load retailer payouts');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleInitiatePayout = async () => {
    if (!selectedRetailer || !payoutAmount) return;
    
    const amount = parseFloat(payoutAmount);
    if (isNaN(amount) || amount <= 0) {
      toast.error('Please enter a valid payout amount');
      return;
    }

    if (amount > selectedRetailer.pending_payout) {
      toast.error('Payout amount cannot exceed pending balance');
      return;
    }

    try {
      setActionLoading(true);
      await admin.initiateRetailerPayout(selectedRetailer.id, amount);
      toast.success(`Payout of $${amount.toFixed(2)} initiated for ${selectedRetailer.store_name || selectedRetailer.name}`);
      setPayoutAmount('');
      loadRetailers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initiate payout');
    } finally {
      setActionLoading(false);
    }
  };

  const filteredRetailers = retailers.filter(r => {
    if (!filter) return true;
    const searchLower = filter.toLowerCase();
    return (
      r.name?.toLowerCase().includes(searchLower) ||
      r.store_name?.toLowerCase().includes(searchLower) ||
      r.email?.toLowerCase().includes(searchLower)
    );
  });

  const getPaymentMethodBadge = (retailer) => {
    if (retailer.stripe_account_id && retailer.stripe_payouts_enabled) {
      return (
        <Badge className="bg-purple-100 text-purple-800">
          <CreditCard className="w-3 h-3 mr-1" />
          Stripe Connected
        </Badge>
      );
    } else if (retailer.manual_bank_details) {
      return (
        <Badge className="bg-blue-100 text-blue-800">
          <Building2 className="w-3 h-3 mr-1" />
          Manual Bank
        </Badge>
      );
    }
    return (
      <Badge className="bg-gray-100 text-gray-800">
        <AlertCircle className="w-3 h-3 mr-1" />
        No Payment Setup
      </Badge>
    );
  };

  const getPayoutStatus = (retailer) => {
    if (!retailer.payment_method) return 'pending_setup';
    if (retailer.pending_payout > 0) return 'ready';
    return 'ready';
  };

  return (
    <AdminLayout user={user} onLogout={onLogout}>
      <div className="space-y-6">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-500 to-indigo-600 rounded-2xl p-8 text-white shadow-lg">
          <h2 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
            Retailer Payouts
          </h2>
          <p className="text-blue-50 text-lg">Manage retailer earnings and process payouts</p>
        </div>

        {/* Summary Stats */}
        <div className="grid md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="bg-yellow-100 p-3 rounded-lg">
                  <Clock className="w-6 h-6 text-yellow-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Pending Payouts</p>
                  <p className="text-2xl font-bold text-yellow-600">${totals.pending?.toFixed(2) || '0.00'}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="bg-green-100 p-3 rounded-lg">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Total Paid Out</p>
                  <p className="text-2xl font-bold text-green-600">${totals.paid?.toFixed(2) || '0.00'}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="bg-blue-100 p-3 rounded-lg">
                  <TrendingUp className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Total Earnings</p>
                  <p className="text-2xl font-bold text-blue-600">${totals.total?.toFixed(2) || '0.00'}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Search */}
        <Card>
          <CardContent className="pt-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                placeholder="Search retailers..."
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="pl-10"
                data-testid="search-retailer-payouts"
              />
            </div>
          </CardContent>
        </Card>

        {/* Main Content */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Retailers List */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Store className="w-5 h-5 text-blue-600" />
                Retailers ({filteredRetailers.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8 text-gray-500">Loading retailers...</div>
              ) : filteredRetailers.length === 0 ? (
                <div className="text-center py-8 text-gray-500">No retailers found</div>
              ) : (
                <div className="space-y-3 max-h-[500px] overflow-y-auto">
                  {filteredRetailers.map((retailer) => {
                    const status = getPayoutStatus(retailer);
                    const statusConfig = PAYMENT_STATUS[status];
                    const isSelected = selectedRetailer?.id === retailer.id;
                    
                    return (
                      <button
                        key={retailer.id}
                        onClick={() => {
                          setSelectedRetailer(retailer);
                          setPayoutAmount(retailer.pending_payout?.toFixed(2) || '');
                        }}
                        className={`w-full p-4 rounded-lg border text-left transition-all ${
                          isSelected 
                            ? 'border-blue-500 bg-blue-50 shadow-md' 
                            : 'border-gray-200 bg-white hover:border-blue-300'
                        }`}
                        data-testid={`retailer-${retailer.id}`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <h4 className="font-medium text-gray-900 truncate mb-1">
                              {retailer.store_name || retailer.name}
                            </h4>
                            <p className="text-xs text-gray-500 truncate mb-2">{retailer.email}</p>
                            <div className="flex flex-wrap gap-2 mb-2">
                              {getPaymentMethodBadge(retailer)}
                              <Badge className={retailer.store_status === 'live' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                                {retailer.store_status}
                              </Badge>
                            </div>
                            <div className="flex items-center gap-4 text-sm">
                              <span className="text-gray-600">
                                Pending: <span className="font-medium text-yellow-600">${retailer.pending_payout?.toFixed(2) || '0.00'}</span>
                              </span>
                              <span className="text-gray-600">
                                Total: <span className="font-medium text-blue-600">${retailer.total_earnings?.toFixed(2) || '0.00'}</span>
                              </span>
                            </div>
                          </div>
                          <ArrowRight className={`w-5 h-5 flex-shrink-0 ${
                            isSelected ? 'text-blue-600' : 'text-gray-400'
                          }`} />
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Payout Panel */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Send className="w-5 h-5 text-blue-600" />
                Process Payout
              </CardTitle>
            </CardHeader>
            <CardContent>
              {!selectedRetailer ? (
                <div className="text-center py-12 text-gray-500">
                  <DollarSign className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>Select a retailer to process payout</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Retailer Info */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 text-lg mb-1">
                      {selectedRetailer.store_name || selectedRetailer.name}
                    </h4>
                    <p className="text-sm text-gray-600 mb-3">{selectedRetailer.email}</p>
                    {getPaymentMethodBadge(selectedRetailer)}
                  </div>

                  {/* Balance Info */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                      <p className="text-sm text-yellow-700">Pending Payout</p>
                      <p className="text-2xl font-bold text-yellow-700">
                        ${selectedRetailer.pending_payout?.toFixed(2) || '0.00'}
                      </p>
                    </div>
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <p className="text-sm text-green-700">Total Paid</p>
                      <p className="text-2xl font-bold text-green-700">
                        ${selectedRetailer.total_paid?.toFixed(2) || '0.00'}
                      </p>
                    </div>
                  </div>

                  {/* Payment Method Details */}
                  {selectedRetailer.stripe_account_id && selectedRetailer.stripe_payouts_enabled ? (
                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <CreditCard className="w-5 h-5 text-purple-600" />
                        <h5 className="font-medium text-purple-900">Stripe Connect</h5>
                      </div>
                      <p className="text-sm text-purple-700">
                        Account ID: {selectedRetailer.stripe_account_id}
                      </p>
                      <p className="text-sm text-purple-600 mt-1">
                        Payouts will be processed automatically via Stripe
                      </p>
                    </div>
                  ) : selectedRetailer.manual_bank_details ? (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Building2 className="w-5 h-5 text-blue-600" />
                        <h5 className="font-medium text-blue-900">Manual Bank Transfer</h5>
                      </div>
                      <div className="text-sm text-blue-700 space-y-1">
                        <p>Bank: {selectedRetailer.manual_bank_details.bank_name}</p>
                        <p>Account: ****{selectedRetailer.manual_bank_details.bank_account_last4}</p>
                      </div>
                      <p className="text-sm text-blue-600 mt-2">
                        Manual ACH transfer required
                      </p>
                    </div>
                  ) : (
                    <div className="bg-gray-100 border border-gray-300 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <AlertCircle className="w-5 h-5 text-gray-600" />
                        <h5 className="font-medium text-gray-700">No Payment Method</h5>
                      </div>
                      <p className="text-sm text-gray-600">
                        This retailer has not set up payment information yet.
                      </p>
                    </div>
                  )}

                  {/* Payout Form */}
                  {selectedRetailer.payment_method && selectedRetailer.pending_payout > 0 && (
                    <div className="border-t pt-4">
                      <label className="text-sm font-medium text-gray-700 mb-2 block">
                        Payout Amount
                      </label>
                      <div className="flex gap-3">
                        <div className="relative flex-1">
                          <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            max={selectedRetailer.pending_payout}
                            value={payoutAmount}
                            onChange={(e) => setPayoutAmount(e.target.value)}
                            className="pl-10"
                            placeholder="0.00"
                            data-testid="retailer-payout-amount"
                          />
                        </div>
                        <Button
                          onClick={handleInitiatePayout}
                          disabled={actionLoading || !payoutAmount}
                          className="bg-blue-600 hover:bg-blue-700"
                          data-testid="initiate-retailer-payout-btn"
                        >
                          {actionLoading ? (
                            <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                          ) : (
                            <Send className="w-4 h-4 mr-2" />
                          )}
                          {actionLoading ? 'Processing...' : 'Initiate Payout'}
                        </Button>
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        Max payout: ${selectedRetailer.pending_payout?.toFixed(2)}
                      </p>
                    </div>
                  )}

                  {/* Recent Payouts */}
                  {selectedRetailer.recent_payouts?.length > 0 && (
                    <div className="border-t pt-4">
                      <h5 className="font-medium text-gray-900 mb-3">Recent Payouts</h5>
                      <div className="space-y-2">
                        {selectedRetailer.recent_payouts.map((payout, idx) => (
                          <div key={idx} className="flex items-center justify-between text-sm bg-gray-50 p-3 rounded">
                            <span className="text-gray-600">
                              {new Date(payout.date).toLocaleDateString()}
                            </span>
                            <span className="font-medium text-blue-600">
                              ${payout.amount.toFixed(2)}
                            </span>
                            <Badge className={payout.status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}>
                              {payout.status}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </AdminLayout>
  );
}
